// Which price to show on the CURRENT-plan card (deferred close-out Task 3,
// 2026-08-11 — operator decision, fix round 1 2026-08-11). Pure so it's
// covered by this package's `node --test` suite, and a SINGLE copy: it is
// imported by both the dashboard worker (@pageboss/dashboard, apps/dashboard/
// src) and the dashboard SPA (apps/dashboard/web) the same way PLANS already
// is — no hand-mirrored duplicate on this money path.
//
// The rule is deliberately simple: show the STORED charged amount when we
// have one, otherwise show nothing. Never fall back to PLANS[plan]'s list
// price — Stripe prices are immutable, so after a repricing an existing
// subscriber may be paying a price that no longer appears anywhere in PLANS,
// and guessing would show them a number they don't actually pay.

export interface ChargedPriceDisplay {
  amountCents: number;
  interval: 'month' | 'year';
}

/**
 * Decide the CURRENT-plan card's price display from the stored
 * `subscriptions.chargedAmountCents` / `chargedInterval` columns.
 *
 * Returns null (hide the figure) when either value is missing or the
 * interval isn't one of the two Stripe-recurring values we store — covers a
 * row that predates this column, a row the webhook has never touched yet, and
 * a comped org (no Stripe subscription, so no webhook ever fires for it).
 *
 * `amountCents: 0` is a real, showable value (not treated as "missing") —
 * only null/undefined/non-finite counts as unknown.
 */
export function chargedPriceDisplay(
  chargedAmountCents: number | null | undefined,
  chargedInterval: string | null | undefined,
): ChargedPriceDisplay | null {
  if (typeof chargedAmountCents !== 'number' || !Number.isFinite(chargedAmountCents)) return null;
  if (chargedInterval !== 'month' && chargedInterval !== 'year') return null;
  return { amountCents: chargedAmountCents, interval: chargedInterval };
}

/**
 * Format a real charged amount as a dollar string WITHOUT rounding it.
 *
 * `PLANS[plan].monthlyPriceCents` values are always whole dollars, so the
 * pre-existing `price()` helper in billing.tsx could get away with
 * `.toFixed(0)`. `chargedAmountCents` is an arbitrary historical Stripe
 * amount — that's the whole premise of this column — and rounding it (e.g.
 * $19.99 -> "$20") would display a number the customer was never actually
 * charged, exactly the failure mode this feature exists to prevent. Show
 * cents only when the amount isn't a whole dollar.
 */
export function formatChargedPrice(amountCents: number): string {
  const dollars = amountCents / 100;
  return amountCents % 100 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
}
